import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  CheckCircle2,
  Flag,
  Inbox,
  Filter,
} from 'lucide-react';
import { useReviewQueueStore } from '@/stores/reviewQueue';
import { useKnowledgeStore } from '@/stores/knowledge';
import { ReviewQueueList } from '@/components/knowledge/resolution/ReviewQueueList';
import { ReviewItem } from '@/components/knowledge/resolution/ReviewItem';

const DECISIONS = [
  { value: '', label: 'All decisions' },
  { value: 'merge', label: 'Merge' },
  { value: 'link', label: 'Link' },
  { value: 'distinct', label: 'Distinct' },
  { value: 'promote', label: 'Promote' },
];

const ORDERS: Array<{ value: 'confidence_asc' | 'confidence_desc' | 'created_desc' | 'created_asc'; label: string }> = [
  { value: 'confidence_asc', label: 'Confidence ↑ (uncertain first)' },
  { value: 'confidence_desc', label: 'Confidence ↓' },
  { value: 'created_desc', label: 'Newest first' },
  { value: 'created_asc', label: 'Oldest first' },
];

export function ReviewQueuePage() {
  const { filters, stats, setFilters, fetchQueue, fetchStats } =
    useReviewQueueStore();
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchQueue();
    fetchStats();
    fetchEntityTypes();
  }, [fetchQueue, fetchStats, fetchEntityTypes]);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/resolution"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Resolution Pipeline
          </Link>
          <div className="flex items-center gap-2">
            <Inbox size={18} className="text-brand-teal" />
            <div>
              <h1 className="text-xl font-semibold text-navy-900">Review Queue</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Human-in-the-loop verdicts on LLM resolution decisions
              </p>
            </div>
          </div>
        </div>
        {stats && (
          <div className="flex items-center gap-4">
            <StatTile
              label="Unreviewed"
              value={stats.unreviewed.toLocaleString()}
            />
            <StatTile
              label="Reviewed today"
              value={stats.reviewed_today.toLocaleString()}
            />
            <StatTile
              label="Override rate"
              value={`${(stats.override_rate * 100).toFixed(0)}%`}
              tint={
                stats.override_rate > 0.2
                  ? 'rose'
                  : stats.override_rate > 0.1
                    ? 'amber'
                    : 'teal'
              }
            />
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <CheckCircle2 size={11} className="text-emerald-500" />
              {stats.reviewed.toLocaleString()} total
              <Flag size={11} className="text-amber-500 ml-1" />
              {stats.flagged}
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-100 bg-white flex-wrap">
        <Filter size={12} className="text-slate-400" />
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={!filters.human_reviewed}
            onChange={(e) =>
              setFilters({ human_reviewed: !e.target.checked })
            }
            className="accent-brand-teal"
          />
          Unreviewed only
        </label>
        <select
          value={filters.decision}
          onChange={(e) => setFilters({ decision: e.target.value })}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          {DECISIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <select
          value={filters.entity_type}
          onChange={(e) => setFilters({ entity_type: e.target.value })}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filters.order_by}
          onChange={(e) =>
            setFilters({
              order_by: e.target.value as typeof filters.order_by,
            })
          }
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          {ORDERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1 min-h-0">
        <div className="w-[38%] p-4 overflow-auto">
          <ReviewQueueList />
        </div>
        <div className="flex-1 min-w-0 bg-white border-l border-slate-200 overflow-hidden">
          <ReviewItem />
        </div>
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  tint = 'slate',
}: {
  label: string;
  value: string;
  tint?: 'slate' | 'teal' | 'amber' | 'rose';
}) {
  const colorMap: Record<string, string> = {
    slate: 'text-navy-900',
    teal: 'text-brand-teal-dark',
    amber: 'text-amber-600',
    rose: 'text-rose-600',
  };
  return (
    <div className="text-right">
      <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
        {label}
      </div>
      <div className={clsx('text-lg font-bold', colorMap[tint])}>{value}</div>
    </div>
  );
}
